import { Spacer, VDBText } from "@components/atoms";
import useScreenOptionsHooks from "../../../../../hooks/useScreenOptionsHooks";
import { SpaceType } from "@components/atoms/Spacer/types";
import { Platform, TouchableOpacity, View } from "react-native";
import styled from "styled-components/native";
import { DefaultTheme } from "styled-components/native";
import { css } from "styled-components/native";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";

const PlanStatusContainer = ({
  onHeaderButton,
  getbuttonText,
  isExpired,
  isTrial,
  getStatusColor,
  getStatusLabel,
  getRenewalLabel,
  expiryDate,
  daysLeft,
  daysLeftNoDate,
  showError,
}: {
  onHeaderButton: (action: string) => void;
  getbuttonText: () => string;
  isExpired: boolean;
  isTrial: boolean;
  getStatusColor: () => string;
  getStatusLabel: () => string;
  getRenewalLabel: () => string;
  expiryDate: string;
  daysLeft: number;
  daysLeftNoDate: string;
  showError: boolean;
}) => {
  const { typography, theme, t } = useScreenOptionsHooks();
  const userInfo = useUserInfo();
  const currentPlan = userInfo.user.plan_details?.plan;
  const planType = currentPlan?.name;
  const showButton =
    ((((userInfo.isActiveFreeTrial() &&
      userInfo.user.plan_details.upcoming_subscription === null) ||
      isExpired) &&
      !userInfo.isUpcomingNotPaidPlan()) ||
      userInfo.isUpcomingNotPaidPlan()) &&
    currentPlan?.plan_type !== "saas_plan";

  const PlanInfoSection = ({
    label,
    children,
    variant,
  }: {
    label: React.ReactNode;
    children: React.ReactNode;
    variant?: "plan" | "status" | "renewal";
  }) => {
    return (
      <PlanInfoColumn
        style={{
          flexDirection: "row",
          gap: SpaceType.Double,
          flexWrap: "wrap",
          ...(theme.isMobile && variant !== "plan" && { alignItems: "center" }),
        }}
      >
        <PlanInfoHeader>{label}</PlanInfoHeader>
        <PlanInfoValue>{children}</PlanInfoValue>
      </PlanInfoColumn>
    );
  };

  const RenewalInfoSection = () => {
    return (
      <DateRow>
        {isExpired && (
          <>
            <VDBIcon
              name={VDBIcons.WARNING}
              color={theme["error-01"]}
              size={SpaceType.Double}
              variant={IconsVariant.VDBCustom}
            />
            <VDBText type={typography["Body-M"]} color={theme["error-01"]}>
              {t("account.userList.expired.text", "Expired on")}
            </VDBText>
          </>
        )}

        <VDBText
          type={typography["Body-M"]}
          color={isExpired ? theme["error-01"] : theme["core-05"]}
        >
          {expiryDate || "-"}
        </VDBText>

        {!isExpired && daysLeft > 0 && (
          <DaysRemainingBadge showError={showError}>
            {daysLeft < 10 && (
              <VDBIcon
                name={VDBIcons.WARNING}
                color={theme["error-01"]}
                size={SpaceType.Double}
                variant={IconsVariant.VDBCustom}
              />
            )}
            <VDBText
              type={typography["Body-R"]}
              color={
                showError || daysLeft < 10
                  ? theme["error-01"]
                  : theme["core-06"]
              }
            >
              {daysLeft < 10 ? "" : "("}
              {daysLeftNoDate}{" "}
              {t("account.userList.remaining.text", "remaining")}{" "}
              {daysLeft < 10 ? "" : ")"}
            </VDBText>
          </DaysRemainingBadge>
        )}
      </DateRow>
    );
  };

  const ManagePlanButtonSection = () => {
    return (
      <>
        {Platform.OS === "web" && showButton && (
          <TouchableOpacity onPress={() => onHeaderButton("manage_plan")}>
            <VDBText type={typography["Body-M"]} color={theme["link-static"]}>
              {getbuttonText()}
            </VDBText>
          </TouchableOpacity>
        )}
      </>
    );
  };

  return (
    <PlanInfoCard>
      {theme.isMobile ? (
        <>
          {/* PLAN */}
          <PlanInfoSection
            label={
              <>
                <VDBText type={typography["Body-R"]} color={theme["core-06"]}>
                  {t("account.userList.planLabel.text", "VDB Marketplace Plan")}
                </VDBText>
                {Platform.OS === "web" && showButton && (
                  <>
                    <Spacer size={SpaceType.Basic} />
                    <ManagePlanButtonSection />
                  </>
                )}
              </>
            }
            variant="plan"
          >
            <View
              style={{
                flexDirection: "row",
                gap: SpaceType.Double,
                flexWrap: "wrap",
              }}
            >
              <VDBText type={typography["Body-M"]} color={theme["core-05"]}>
                {planType || "-"}
              </VDBText>
            </View>
          </PlanInfoSection>
          {theme.isMobile && <Spacer size={SpaceType.Double} />}
          {/* STATUS */}
          <PlanInfoSection
            label={
              <VDBText type={typography["Body-R"]} color={theme["core-06"]}>
                {t("account.userList.statusLabel.text", "Status")}
              </VDBText>
            }
          >
            <StatusBadgeSmall
              status={isExpired ? "expired" : isTrial ? "trial" : "active"}
            >
              <VDBText type={typography["Body-M"]} color={getStatusColor()}>
                {getStatusLabel()}
              </VDBText>
            </StatusBadgeSmall>
          </PlanInfoSection>
          {theme.isMobile && <Spacer size={SpaceType.Double} />}
          {/* RENEWAL */}
          <PlanInfoSection
            label={
              <VDBText type={typography["Body-R"]} color={theme["core-06"]}>
                {getRenewalLabel()}
              </VDBText>
            }
          >
            <RenewalInfoSection />
          </PlanInfoSection>
        </>
      ) : (
        <>
          {/* DESKTOP/TABLET (unchanged structure) */}
          <PlanInfoRow
            style={{ borderBottomWidth: 1, borderColor: theme["core-03"] }}
          >
            <PlanInfoColumn>
              <PlanInfoHeader>
                <VDBText type={typography["Body-R"]} color={theme["core-06"]}>
                  {t("account.userList.planLabel.text", "VDB Marketplace Plan")}
                </VDBText>
              </PlanInfoHeader>
            </PlanInfoColumn>

            <PlanInfoColumn variant="status">
              <PlanInfoHeader>
                <VDBText type={typography["Body-R"]} color={theme["core-06"]}>
                  {t("account.userList.statusLabel.text", "Status")}
                </VDBText>
              </PlanInfoHeader>
            </PlanInfoColumn>

            <PlanInfoColumn>
              <PlanInfoHeader>
                <VDBText type={typography["Body-R"]} color={theme["core-06"]}>
                  {getRenewalLabel()}
                </VDBText>
              </PlanInfoHeader>
            </PlanInfoColumn>
          </PlanInfoRow>

          <PlanInfoRow>
            {/* PLAN */}
            <PlanInfoColumn>
              <PlanInfoValue
                style={{ flexDirection: "row", gap: SpaceType.Double }}
              >
                <VDBText type={typography["Body-M"]} color={theme["core-05"]}>
                  {planType || "-"}
                </VDBText>
                <ManagePlanButtonSection />
              </PlanInfoValue>
            </PlanInfoColumn>

            {/* STATUS */}
            <PlanInfoColumn variant="status">
              <PlanInfoValue>
                <StatusBadgeSmall
                  status={isExpired ? "expired" : isTrial ? "trial" : "active"}
                >
                  <VDBText type={typography["Body-M"]} color={getStatusColor()}>
                    {getStatusLabel()}
                  </VDBText>
                </StatusBadgeSmall>
              </PlanInfoValue>
            </PlanInfoColumn>

            {/* RENEWAL */}
            <PlanInfoColumn>
              <PlanInfoValue>
                <RenewalInfoSection />
              </PlanInfoValue>
            </PlanInfoColumn>
          </PlanInfoRow>
        </>
      )}
    </PlanInfoCard>
  );
};

const PlanInfoCard = styled.View`
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  padding: ${SpaceType.Double}px;
  border-radius: ${SpaceType.Basic}px;
`;

const PlanInfoRow = styled.View`
  flex-direction: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isMobile ? "column" : "row"};
  justify-content: space-between;
  align-items: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isMobile ? "stretch" : "flex-start"};
  gap: ${({ theme }: { theme: DefaultTheme }) => (theme.isMobile ? 20 : 16)}px;
`;

const PlanInfoColumn = styled.View<{ variant?: "status" }>`
  ${({ theme, variant }: { theme: DefaultTheme; variant?: "status" }) =>
    theme.isMobile
      ? css`
          width: 100%;
        `
      : variant === "status" && theme.isTablet
      ? css`
          width: 20%;
          min-width: 100px;
        `
      : css`
          flex: 1;
          min-width: 150px;
        `}
`;

const PlanInfoHeader = styled.View`
  ${({ theme }: { theme: DefaultTheme }) =>
    !theme.isMobile &&
    css`
      padding-bottom: ${SpaceType.Double}px;
    `}
`;

const PlanInfoValue = styled.View`
  padding-top: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isMobile ? 0 : SpaceType.Double}px;
`;

const StatusBadgeSmall = styled.View<{ status: string }>`
  align-self: flex-start;
  padding: ${({ theme }: { theme: DefaultTheme }) =>
    theme.isMobile ? "4px 10px" : "2px 8px"};
  border-radius: 24px;
  background-color: ${({
    status,
    theme,
  }: {
    status: string;
    theme: DefaultTheme;
  }) =>
    status === "active" || status === "trial"
      ? theme["success-02"]
      : theme["error-02"]};
`;

const DateRow = styled.View`
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: ${SpaceType.Basic}px;
`;

const DaysRemainingBadge = styled.View<{ showError: boolean }>`
  flex-direction: row;
  align-items: center;
  gap: ${SpaceType.ExtraSmall}px;
`;

export default PlanStatusContainer;
